import { Bath, Trash2, Power, Lock, KeyRound, Car, Utensils, Luggage } from 'lucide-react';

// Master list — default logical order: kitchen/tidy tasks first, final walkthrough
// before locking up, then handing back access items last.
export const CHECKOUT_INSTRUCTIONS = [
  { key: 'dishes', label: 'Dishes: wash or load the dishwasher', Icon: Utensils },
  { key: 'towels', label: 'Gather used towels', Icon: Bath },
  { key: 'rubbish', label: 'Throw rubbish away', Icon: Trash2 },
  { key: 'turn_off', label: 'Turn things off', Icon: Power },
  { key: 'belongings', label: 'Take all your belongings with you', Icon: Luggage },
  { key: 'lock_up', label: 'Lock up', Icon: Lock },
  { key: 'return_keys', label: 'Return keys', Icon: KeyRound },
  { key: 'parking_pass', label: 'Return parking pass / fob', Icon: Car },
];

// Normalise legacy data (array of string keys) and new data (array of {key, note})
// into a consistent ordered array of { key, note }.
export const normalizeInstructions = (arr) =>
  (arr || [])
    .map((it) => (typeof it === 'string' ? { key: it, note: '' } : { key: it?.key, note: it?.note || '' }))
    .filter((it) => it.key);

const MAX_NOTE = 50;

export default function CheckoutInstructionsPicker({ selected = [], onChange }) {
  // `selected` is an ordered array of { key, note }. Order = selection order = guest display order.
  const items = normalizeInstructions(selected);
  const indexOf = (key) => items.findIndex((s) => s.key === key);

  const toggle = (key) => {
    const i = indexOf(key);
    if (i === -1) onChange([...items, { key, note: '' }]);
    else onChange(items.filter((s) => s.key !== key));
  };

  const setNote = (key, note) => {
    onChange(items.map((s) => (s.key === key ? { ...s, note } : s)));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="checkout-instructions-picker">
      {CHECKOUT_INSTRUCTIONS.map(({ key, label, Icon }) => {
        const idx = indexOf(key);
        const isOn = idx !== -1;
        const note = isOn ? items[idx].note || '' : '';
        return (
          <div
            key={key}
            className={`relative rounded-2xl border-2 transition-all ${
              isOn
                ? 'border-transparent bg-gradient-to-r from-blue-500/10 to-emerald-500/10 ring-2 ring-emerald-400'
                : 'border-slate-200 bg-white hover:border-emerald-300'
            }`}
          >
            <button
              type="button"
              onClick={() => toggle(key)}
              className="w-full flex items-center gap-3 p-3 text-left active:scale-[0.98]"
              data-testid={`checkout-instruction-${key}`}
            >
              <span
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                  isOn ? 'bg-gradient-to-br from-blue-500 to-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                <Icon size={20} />
              </span>
              <span className={`text-sm font-medium pr-6 ${isOn ? 'text-slate-800' : 'text-slate-600'}`}>{label}</span>
              {isOn && (
                <span
                  className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[11px] font-bold"
                  data-testid={`checkout-instruction-number-${key}`}
                >
                  {idx + 1}
                </span>
              )}
            </button>
            {isOn && (
              <div className="px-3 pb-3 -mt-1">
                <input
                  type="text"
                  value={note}
                  maxLength={MAX_NOTE}
                  onChange={(e) => setNote(key, e.target.value)}
                  placeholder="Add a note for this property (optional)"
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid={`checkout-instruction-note-${key}`}
                />
                <div className="flex justify-end mt-0.5">
                  <span
                    className={`text-[10px] font-medium ${
                      note.length >= MAX_NOTE ? 'text-red-500' : note.length >= MAX_NOTE - 7 ? 'text-amber-500' : 'text-slate-400'
                    }`}
                    data-testid={`checkout-instruction-note-counter-${key}`}
                  >
                    {note.length}/{MAX_NOTE}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
